import type { MetaFunction } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";

import { Button } from "~/components/button";
import { ContactSection } from "~/components/contact-section";
import { Container } from "~/components/container";
import { Delivery } from "~/components/delivery";
import { FadeIn, FadeInStagger } from "~/components/fade-in";
import { List, ListItem } from "~/components/list";
import { LogoWall } from "~/components/logo-wall";
import { ModuleDiagram } from "~/components/module-diagram";
import { SectionIntro } from "~/components/section-intro";
import { StatList, StatListItem } from "~/components/stat-list";
import { Testimonial } from "~/components/testimonial";
import { clientsLight, testimonials } from "~/lib/client.server";
import type { MDXCaseStudy } from "~/lib/mdx.server";
import { loadCaseStudies } from "~/lib/mdx.server";
import { delivery } from "~/lib/site";

export const meta: MetaFunction = () => {
  return [
    {
      title: `bmcreations — Kotlin Multiplatform app delivery for iOS and Android`,
    },
    {
      name: "description",
      content: `We ship iOS and Android apps from one Kotlin codebase. Senior engineers who embed with your team, put a build in testers' hands in week two, and release every week after that.`,
    },
    { name: "theme-color", content: "#08090B" },
  ];
};

function Hero() {
  let data = useLoaderData<typeof loader>();

  return (
    <Container className="pt-16 sm:pt-24 lg:pt-28">
      <FadeIn className="max-w-4xl">
        <p className="label text-accent-400">
          Kotlin Multiplatform · iOS &amp; Android
        </p>
        <h1 className="mt-6 font-display text-5xl font-medium tracking-tight text-ink-50 [text-wrap:balance] sm:text-6xl lg:text-7xl">
          Senior mobile delivery. Two platforms, one Kotlin codebase.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-ink-300">
          We embed with your engineers, move the logic layer into shared Kotlin,
          and put an installable build in testers&rsquo; hands in week two.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Button to="/contact" size="lg">
            {delivery.callLabel}
          </Button>
          <Button to="#delivery" size="lg" variant="outline">
            How we deliver
          </Button>
        </div>
        <p className="mt-6 text-sm text-ink-400">
          Reply within {delivery.responseTime}, from the engineers who&rsquo;d
          do the work.
        </p>
      </FadeIn>

      <FadeIn className="mt-16 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 sm:mt-20">
        <p className="label text-ink-400">Shipped for</p>
        <p className="label text-ink-400">
          {data.clients.length} teams · 500,000+ users · 2 store features
        </p>
      </FadeIn>

      <LogoWall clients={data.clients} className="mt-5" />
    </Container>
  );
}

function Engagements() {
  return (
    <>
      <SectionIntro
        eyebrow="Engagements"
        title="Four ways teams bring us in."
        className="mt-24 sm:mt-32"
      >
        <p>
          Whether you are starting from a blank repo or a stalled release, the
          work is the same: get something shippable in front of real users,
          fast.
        </p>
      </SectionIntro>
      <Container className="mt-12">
        <List className="max-w-3xl">
          <ListItem title="Ship a new app">
            Blank repo to store listing. Architecture, design, build and
            submission, with a shared Kotlin core from day one so the second
            platform costs a fraction of the first.
          </ListItem>
          <ListItem title="Add Kotlin Multiplatform">
            You already have iOS and Android apps drifting apart. We carve out
            the shared layer feature by feature, keeping both apps releasable
            the whole way through.
          </ListItem>
          <ListItem title="Rescue a stalled release">
            Flaky builds, a crash rate nobody can explain, a deadline that
            already slipped. We come in, find the real blocker, and get you back
            to shipping weekly.
          </ListItem>
          <ListItem title="Embed senior engineers">
            Extra mobile depth on your team, in your process — code reviews,
            technical direction and mentoring included, not billed as extras.
          </ListItem>
        </List>

        <FadeIn className="mt-12 max-w-3xl rounded-xl border border-ink-700 bg-ink-900 p-6">
          <p className="text-base text-ink-300">
            <strong className="font-semibold text-ink-50">
              KMP is not always the answer.
            </strong>{" "}
            If fully native or something else is the faster path for what you
            are building, we will tell you on the first call — and point you at
            the people who do it well.
          </p>
        </FadeIn>
      </Container>
    </>
  );
}

function CaseStudies({ caseStudies }: { caseStudies: Array<MDXCaseStudy> }) {
  if (caseStudies.length === 0) return null;

  return (
    <>
      <SectionIntro
        eyebrow="Case studies"
        title="Work that made it to the store."
        className="mt-24 sm:mt-32"
      />
      <Container className="mt-12">
        <FadeInStagger className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {caseStudies.map((caseStudy) => (
            <FadeIn key={caseStudy.href} className="flex">
              <article className="relative flex w-full flex-col rounded-xl border border-ink-700 bg-ink-900 p-6 transition hover:border-ink-600">
                <h3>
                  <Link to={caseStudy.href}>
                    <span className="absolute inset-0 rounded-xl" />
                    <img
                      src={caseStudy.logo}
                      alt={caseStudy.client}
                      className="h-12 w-12"
                    />
                  </Link>
                </h3>
                <p className="label mt-6 text-ink-400">
                  <time dateTime={caseStudy.date.split("-")[0]}>
                    {caseStudy.date.split("-")[0]}
                  </time>
                  <span className="px-2 text-ink-600">/</span>
                  Case study
                </p>
                <p className="mt-4 font-display text-xl font-semibold text-ink-50">
                  {caseStudy.title}
                </p>
                <p className="mt-3 text-base text-ink-300">
                  {caseStudy.description}
                </p>
              </article>
            </FadeIn>
          ))}
        </FadeInStagger>
      </Container>
    </>
  );
}

export async function loader() {
  let caseStudies = await loadCaseStudies();
  return {
    caseStudies: caseStudies.slice(0, 3),
    testimonial: {
      client: { name: "Gabb", logo: "client-gabb-logo-light" },
      message: testimonials["Gabb"],
    },
    clients: clientsLight,
  } as const;
}

export default function Home() {
  let data = useLoaderData<typeof loader>();

  return (
    <>
      <Hero />

      <Container className="mt-20 sm:mt-28">
        <StatList>
          <StatListItem
            value={String(data.clients.length)}
            label="Product teams shipped for"
          />
          <StatListItem value="500,000+" label="People using what we built" />
          <StatListItem value="2" label="App Store features and awards" />
        </StatList>
      </Container>

      <ModuleDiagram className="mt-24 sm:mt-32" />

      <Delivery className="mt-24 sm:mt-32" />

      <Engagements />

      <CaseStudies caseStudies={data.caseStudies} />

      <Testimonial className="mt-24 sm:mt-32" client={data.testimonial.client}>
        {data.testimonial.message}
      </Testimonial>

      <ContactSection />
    </>
  );
}
